import { useRef, useState, useCallback, type ChangeEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { GoogleDrivePicker } from "@/components/GoogleDrivePicker";
import {
    importDriveFilesToTopic,
    importLocalFilesToTopic,
    type DriveFileWithText,
} from "@/lib/topicKbDocumentImport";
import { FolderOpen, Loader2, Upload } from "lucide-react";
import { SiGoogledrive } from "react-icons/si";

const LOCAL_FILE_ACCEPT =
    ".pdf,.docx,.doc,.txt,.md,.markdown,.json,.csv,.jpeg,.jpg,.png,.gif,.webp,.bmp,.svg,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,text/*,application/json,text/csv,image/*";

export interface TopicDocumentModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    spaceId: string | null;
    topicId: string;
    workspaceId: string | null;
    onFinished?: () => void | Promise<void>;
}

export function TopicDocumentModal({
    open,
    onOpenChange,
    spaceId,
    topicId,
    workspaceId,
    onFinished,
}: TopicDocumentModalProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const folderInputRef = useRef<HTMLInputElement>(null);
    const [isBusy, setIsBusy] = useState(false);

    const runLocalImport = useCallback(
        async (files: File[]) => {
            if (files.length === 0) return;
            setIsBusy(true);
            try {
                const { successCount, failCount } = await importLocalFilesToTopic(files, {
                    spaceId,
                    topicId,
                    workspaceId,
                    queryClient,
                    onRefresh: onFinished,
                });
                if (successCount === 0 && failCount === 0) {
                    toast({
                        title: "No supported files",
                        description: "No files matched supported types (PDF, Office, text, common images, etc.).",
                        variant: "destructive",
                    });
                    return;
                }
                if (successCount > 0) {
                    toast({
                        title: "Import complete",
                        description: `${successCount} document(s) added and linked to this topic${failCount > 0 ? `. ${failCount} failed.` : "."}`,
                    });
                    await onFinished?.();
                    onOpenChange(false);
                }
                if (failCount > 0 && successCount === 0) {
                    toast({
                        title: "Import failed",
                        description: `Could not process ${failCount} file(s).`,
                        variant: "destructive",
                    });
                }
            } finally {
                setIsBusy(false);
            }
        },
        [spaceId, topicId, workspaceId, queryClient, onFinished, onOpenChange, toast]
    );

    const onFileInputChange = async (e: ChangeEvent<HTMLInputElement>) => {
        const list = e.target.files;
        if (fileInputRef.current) fileInputRef.current.value = "";
        if (!list?.length) return;
        await runLocalImport(Array.from(list));
    };

    const onFolderInputChange = async (e: ChangeEvent<HTMLInputElement>) => {
        const list = e.target.files;
        if (folderInputRef.current) folderInputRef.current.value = "";
        if (!list?.length) return;
        await runLocalImport(Array.from(list));
    };

    const onDriveFilesSelected = async (
        files: Array<{ filename: string; text: string; mimeType?: string; error?: string }>
    ) => {
        setIsBusy(true);
        try {
            const { successCount, failCount } = await importDriveFilesToTopic(files as DriveFileWithText[], {
                spaceId,
                topicId,
                workspaceId,
                queryClient,
                onRefresh: onFinished,
            });
            if (successCount === 0 && failCount === 0) {
                toast({
                    title: "No valid files",
                    description: "No files with extractable text were found.",
                    variant: "destructive",
                });
                return;
            }
            if (successCount > 0) {
                toast({
                    title: "Notes created",
                    description: `${successCount} note(s) created and linked to this topic${failCount > 0 ? `. ${failCount} failed.` : "."}`,
                });
                await onFinished?.();
                onOpenChange(false);
            }
            if (failCount > 0 && successCount === 0) {
                toast({
                    title: "Creation failed",
                    description: `Failed to create ${failCount} note(s).`,
                    variant: "destructive",
                });
            }
        } finally {
            setIsBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" onClick={(e) => e.stopPropagation()}>
                <DialogHeader>
                    <DialogTitle>Add new document</DialogTitle>
                    <DialogDescription>
                        Create or import documents into this space and link them to this topic.
                    </DialogDescription>
                </DialogHeader>
                <Tabs defaultValue="files" className="w-full">
                    <TabsList className="grid w-full grid-cols-3">
                        <TabsTrigger value="files" disabled={isBusy} data-testid="tab-topic-doc-files">
                            Files
                        </TabsTrigger>
                        <TabsTrigger value="folder" disabled={isBusy} data-testid="tab-topic-doc-folder">
                            Folder
                        </TabsTrigger>
                        <TabsTrigger value="drive" disabled={isBusy} data-testid="tab-topic-doc-drive">
                            Drive
                        </TabsTrigger>
                    </TabsList>
                    <TabsContent value="files" className="mt-4 space-y-3">
                        <div>
                            <h4 className="text-sm font-medium mb-1">Select files</h4>
                            <p className="text-sm text-muted-foreground mb-3">
                                Pick individual documents, images, or PDFs to import
                            </p>
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                accept={LOCAL_FILE_ACCEPT}
                                className="hidden"
                                onChange={onFileInputChange}
                                disabled={isBusy}
                            />
                            <Button
                                type="button"
                                variant="outline"
                                className="w-full justify-start"
                                disabled={isBusy}
                                onClick={() => fileInputRef.current?.click()}
                                data-testid="button-topic-doc-select-files"
                            >
                                {isBusy ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin shrink-0" />
                                ) : (
                                    <Upload className="h-4 w-4 mr-2 shrink-0" />
                                )}
                                Choose files
                            </Button>
                        </div>
                    </TabsContent>
                    <TabsContent value="folder" className="mt-4 space-y-3">
                        <div>
                            <h4 className="text-sm font-medium mb-1">Import folder</h4>
                            <p className="text-sm text-muted-foreground mb-3">
                                Select a folder to import all supported files inside it (same types as individual
                                files).
                            </p>
                            <input
                                ref={folderInputRef}
                                type="file"
                                {...({ webkitdirectory: "" } as Record<string, unknown>)}
                                multiple
                                className="hidden"
                                onChange={onFolderInputChange}
                                disabled={isBusy}
                            />
                            <Button
                                type="button"
                                variant="outline"
                                className="w-full justify-start"
                                disabled={isBusy}
                                onClick={() => folderInputRef.current?.click()}
                                data-testid="button-topic-doc-select-folder"
                            >
                                {isBusy ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin shrink-0" />
                                ) : (
                                    <FolderOpen className="h-4 w-4 mr-2 shrink-0" />
                                )}
                                Choose folder
                            </Button>
                        </div>
                    </TabsContent>
                    <TabsContent value="drive" className="mt-4 space-y-3">
                        <p className="text-sm text-muted-foreground">
                            Import from Google Drive. Files are extracted as text and saved as notes in this space,
                            then linked to this topic.
                        </p>
                        <GoogleDrivePicker
                            multiple={true}
                            isFinalConfirmation={true}
                            extractTextOnly={true}
                            onFilesSelected={onDriveFilesSelected}
                        >
                            <Button
                                type="button"
                                variant="outline"
                                className="w-full justify-start"
                                disabled={isBusy}
                                data-testid="button-topic-doc-google-drive"
                            >
                                {isBusy ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin shrink-0" />
                                ) : (
                                    <SiGoogledrive className="h-4 w-4 mr-2 shrink-0" />
                                )}
                                Open Google Drive
                            </Button>
                        </GoogleDrivePicker>
                    </TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
    );
}
